import Link from '@components/common/Link/Link'
import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import WishListIcon from '@components/icons/WishListIcon/WishListIcon'

import { getProductName } from '@components/views/Product/helpersv2'
import { CustomProductGridTile } from '@features/products/api/get-custom-product-grid-tiles'
import { formatPriceV2 } from '@lib/format-price'
import { getStockAvailability } from '@lib/get-stock-availability'
import { CustomProductGridTileVariantFragment } from '@lib/graphql/graphql'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import {
  selectAlpha2Code,
  selectChannel,
  selectEcommerceEnabled,
  selectLabels,
} from '@redux/globalSlice'
import { useAppDispatch } from '@redux/store'
import { addItem, removeItem, selectWishlist } from '@redux/wishlistSlice'
import { mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { useQueryState } from 'next-usequerystate'
import { FC, useEffect, useMemo, useRef } from 'react'
import { IoChevronBack, IoChevronForward } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import invariant from 'tiny-invariant'
import Cross from '../Icons/Cross/Cross'
import ProductCartTag from '../Icons/ProductCartTag/ProductCartTag'
import Media from '../Media/Media'
import s from './ProductCardV2.module.scss'

export interface ProductCardV2Props {
  product: CustomProductGridTile & {
    heroVariant: CustomProductGridTileVariantFragment
  }
  loaded?: boolean
  gridSize?: number
  debugMode?: boolean
  wishlistMode?: boolean

  registerKeyHandler: (callback: (e: KeyboardEvent) => void) => () => void
}

type ProductTag = {
  label: string
  labelColor?: string
  fill?: string
}

const ProductCardV2: FC<ProductCardV2Props> = ({
  product,
  loaded,
  debugMode,
  wishlistMode,
  gridSize: calcGridSize,
  registerKeyHandler,
}) => {
  const dispatch = useAppDispatch()
  const ecommerceEnabled = useSelector(selectEcommerceEnabled)
  const wishList = useSelector(selectWishlist)
  const alpha2Code = useSelector(selectAlpha2Code)
  const channel = useSelector(selectChannel)
  const productName = useMemo(() => getProductName(product.name), [product])
  const rootRef = useRef<HTMLLIElement>(null)
  const [gridSize, _] = useQueryState('gridSize')

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
      slides: {
        perView: 1,
      },
      renderMode: 'performance',
      slideChanged: (slider) => {
        // for hover state to work correctly, the data-slide-index needs to be dynamically updated
        slider.container.setAttribute(
          'data-slide-index',
          `${slider.track.details.rel}`,
        )

        const dots = slider.container.querySelectorAll(
          `.${s.imageDots} .${s.imageDot}`,
        )
        dots?.forEach((dot, index) => {
          if (index === slider.track.details.rel) {
            dot.classList.add(s.active)
          } else {
            dot.classList.remove(s.active)
          }
        })
      },
    },
    [],
  )

  useEffect(() => {
    if (!rootRef.current) return
    if (mapEdgesToItems(product.heroVariant.media).length <= 1) return

    // Define the child's key event handler.
    const handleKeyEvent = (e: KeyboardEvent) => {
      // check if rootRef is hovered
      if (!rootRef.current.matches(':hover')) return

      if (e.key === 'ArrowLeft') {
        instanceRef.current?.prev()
      } else if (e.key === 'ArrowRight') {
        instanceRef.current?.next()
      }
    }

    // Register the handler with the parent.
    const unregister = registerKeyHandler(handleKeyEvent)

    // Cleanup by unregistering the callback when the component unmounts.
    return () => {
      unregister()
    }
  }, [
    instanceRef,
    mapEdgesToItems(product.heroVariant.media).length,
    product.id,
    registerKeyHandler,
    rootRef,
  ])

  useEffect(() => {
    instanceRef.current?.update()
  }, [gridSize])

  const media = mapEdgesToItems(product.heroVariant.media)

  const mainMedia = !wishlistMode
    ? (media?.find((media) => media?.fileName?.split('_')[1] === 'V') ??
      media[0])
    : media[0]

  const isMainVideo = mainMedia?.fileName?.split('_')[1] === 'V'

  const mediaSizes = useMemo(
    () =>
      gridSize
        ? `(max-width): 834px 50vw, ${100 / parseInt(gridSize)}vw`
        : '25vw',
    [gridSize],
  )

  const isFilled = !!wishList?.includes(product.heroVariant.id)
  const labels = useSelector(selectLabels)

  const productTags = useMemo(() => {
    const tags: ProductTag[] = []

    // if (
    //   discountDisplay.hasDiscount &&
    //   product.heroVariant.discountedPrice?.value
    // ) {
    //   tags.push({
    //     label: `${
    //       discountDisplay.hasDifferentDiscountsOnVariants ? 'up to' : ''
    //     } ${discountDisplay.highestDiscountPercentage}%`,
    //     fill: 'var(--discount-red)',
    //     labelColor: 'rgb(255, 255, 255)',
    //   })
    // }
    const discountedPrice = product.heroVariant?.price?.discountedPrice
    if (
      discountedPrice &&
      discountedPrice?.discount.value.__typename ===
        'ProductDiscountRelativeValue'
    ) {
      tags.push({
        label: `${discountedPrice?.discount.value?.factor * 100}%`,
        fill: 'var(--discount-red)',
        labelColor: 'rgb(255, 255, 255)',
      })
    }

    if (product.tags.find((tag) => tag === 'label_Responsible')) {
      tags.push({
        label: 'Responsible',
        fill: 'rgb(0,128,0)',
        labelColor: 'rgb(255, 255, 255)',
      })
    }

    if (product.tags?.find((tag) => tag === 'msgtype_New')) {
      tags.push({
        label: (labels.labelMsgTypeNew.find((x) => x) as any).labelName,
      })
    }

    //slice the array because it can max show 2 tags
    return tags.slice(0, 2)
  }, [
    product,
    // discountDisplay
  ])

  const imagePadding = !product.heroVariant?.metadata?.some(
    (x) => x.key === 'DISABLE_IMAGE_PADDING' && x.value === 'true',
  )

  const swatches =
    mapEdgesToItems(
      product.attributeAssignments.find((attribute) => {
        // Check if all values in the attribute satisfy the condition
        return mapEdgesToItems(attribute.values).every((value) => {
          if (value.__typename != 'SwatchAttributeValue') return false

          return value.color || value.media
        })
      })?.values,
    )?.map((value) => {
      invariant(value.__typename === 'SwatchAttributeValue')
      return value.color || value.media.src
    }) ?? []

  if (!mainMedia) return null

  return (
    <li
      className={s.root}
      tabIndex={0}
      // onKeyDown={handleKeyDown}
      // onMouseEnter={handleOnMouseEnter}
      ref={rootRef}
    >
      <div className={clsx(s.gridContent, 'grid-v2-content')}>
        {debugMode && (
          <TrustedHtml
            html={`Color Group: ${
              (product.heroVariant as any)?.colorGroup.name
            }<br>
            Attribute Value: ${
              (product.heroVariant as any)?.colorGroup.attributeValue
            }
            <br>
            Color: ${(product.heroVariant as any)?.colorGroup.color}`}
          />
        )}
        <div className={s.header}>
          {productTags.map((tag, index) => (
            <div key={index} className={s.discountTag}>
              <ProductCartTag
                fill={tag.fill}
                label={tag.label}
                labelColor={tag.labelColor}
              />
            </div>
          ))}

          {ecommerceEnabled && (
            <button
              className={clsx(s.wishlistBtn, { [s.isFilled]: isFilled })}
              aria-label={
                isFilled ? 'Remove from wish list' : 'Add to wish list'
              }
              title={isFilled ? 'Remove from wish list' : 'Add to wish list'}
              onClick={(e) => {
                isFilled
                  ? dispatch(removeItem(product.heroVariant.id))
                  : dispatch(addItem(product.heroVariant.id))
                e.preventDefault()
              }}
            >
              {isFilled && wishlistMode ? (
                <Cross width={calcGridSize == 4 ? 24 : 20} />
              ) : (
                <WishListIcon
                  width={calcGridSize == 4 ? 24 : 20}
                  color="black"
                  filled={isFilled}
                />
              )}
            </button>
          )}
          {swatches && (
            <div className={s.swatches}>
              {swatches?.length > 1 &&
                swatches.slice(0, 6).map((swatch, index) => (
                  <div key={index} className={s.swatch}>
                    <div
                      style={{
                        backgroundImage: swatch.includes('https://')
                          ? `url('${swatch.replace('{size}', '128')}')`
                          : undefined,
                        backgroundColor: swatch ?? undefined,
                        backgroundSize: 'cover',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                      }}
                    />
                  </div>
                ))}
              {swatches?.length > 6 && (
                <div className={s.swatchCount}>+{swatches.length - 6}</div>
              )}
            </div>
          )}
        </div>
        <Link
          link={{ url: `/products/${product.slug}` }}
          query={{
            itemNumber: product.heroVariant.sku,
          }}
          className={s.link}
        >
          <div
            role="button"
            ref={sliderRef}
            data-slide-index={0}
            className={clsx(s.keenSlider, 'keen-slider')}
          >
            <Media
              mediaWithCrops={
                {
                  url: generateMediaUrlV2(mainMedia.src),
                  media: {
                    __typename: 'Image',
                    url: generateMediaUrlV2(mainMedia.src),
                  },
                } as MediaWithCropsFragment
              }
              sizes={mediaSizes}
              innerClassName={clsx({
                [s.specialContainer]: isMainVideo,
              })}
              className={clsx(s.imageContainer, 'keen-slider__slide', {
                [s.grab]: mapEdgesToItems(product.heroVariant.media).length > 1,
              })}
              mediaClassName={clsx(s.image, {
                [s.padded]: !!imagePadding,
                [s.cover]: shouldMediaCover(mainMedia),
              })}
              disableShopTheLook
            >
              {/* {!isMainVideo && hoverImage && (
                <Media
                  mediaWithCrops={
                    {
                      url: generateMediaUrlV2(hoverImage.src),
                      media: {
                        __typename: 'Image',
                        url: generateMediaUrlV2(hoverImage.src),
                      },
                    } as MediaWithCropsFragment
                  }
                  className={clsx(s.imageContainer, s.hoverImageContainer)}
                  mediaClassName={clsx(s.image, {
                    [s.cover]: shouldMediaCover(hoverImage),
                  })}
                  disableShopTheLook
                  sizes="(max-width: 834px) 50vw, 20vw"
                />
              )} */}
              {isMainVideo && (
                <Media
                  mediaWithCrops={
                    {
                      url: generateMediaUrlV2(
                        mapEdgesToItems(product.heroVariant.media).find(
                          (x) => x,
                        )?.src,
                      ),
                      media: {
                        __typename: 'Image',
                        url: generateMediaUrlV2(
                          mapEdgesToItems(product.heroVariant.media).find(
                            (x) => x,
                          )?.src,
                        ),
                      },
                    } as MediaWithCropsFragment
                  }
                  className={clsx(s.imageContainer, s.hoverImageContainer, {
                    [s.grab]:
                      mapEdgesToItems(product.heroVariant.media).length > 1,
                  })}
                  mediaClassName={clsx(s.image, s.hoverImage)}
                  disableShopTheLook
                />
              )}
            </Media>
            {mapEdgesToItems(product.heroVariant.media).length > 1 &&
              mapEdgesToItems(product.heroVariant.media)
                .slice(1)
                .map((media) => (
                  <Media
                    key={product.heroVariant.id + media.fileName + media.src}
                    mediaWithCrops={
                      {
                        url: generateMediaUrlV2(media.src),
                        media: {
                          __typename: 'Image',
                          url: generateMediaUrlV2(media.src),
                        },
                      } as MediaWithCropsFragment
                    }
                    sizes={mediaSizes}
                    className={clsx(s.imageContainer, 'keen-slider__slide', {
                      [s.grab]:
                        mapEdgesToItems(product.heroVariant.media).length > 1,
                    })}
                    mediaClassName={clsx(s.image, {
                      // [s.padded]: !product.heroVariant.disableImagePadding,
                      [s.cover]: shouldMediaCover(media),
                    })}
                    disableShopTheLook
                  />
                ))}

            <div className={s.imageDots}>
              {mapEdgesToItems(product.heroVariant.media).length > 1 &&
                mapEdgesToItems(product.heroVariant.media).map((_, idx) => (
                  <div
                    key={idx}
                    className={clsx(s.imageDot, {
                      [s.active]: idx === 0,
                    })}
                  />
                ))}
            </div>
          </div>
        </Link>
        <div className={s.content}>
          <div className={s.variantData}>
            <div className={s.title}>
              <span>{productName.name}</span>
              <span className={s.secondaryName}>
                {productName.secondaryName}
              </span>
            </div>

            <div>
              <div className={s.price}>
                {product.heroVariant?.price?.discountedPrice && (
                  <span className={s.discountedPrice}>
                    {formatPriceV2(
                      alpha2Code,
                      product.heroVariant.price.discountedPrice.value,
                      2,
                    )}
                  </span>
                )}

                <span
                  className={clsx(s.originalPrice, {
                    [s.strikethrough]:
                      !!product.heroVariant?.price?.discountedPrice,
                  })}
                >
                  {formatPriceV2(alpha2Code, product.heroVariant.price?.value)}
                </span>
              </div>

              <div className={s.stockStatus}>
                {getStockAvailability(channel, product.heroVariant.metadata)}
                {/* {product.heroVariant.stockAvailability.state
                  .split('_')
                  .join(' ')
                  .toLowerCase()} */}
              </div>
            </div>
          </div>
        </div>
        {mapEdgesToItems(product.heroVariant.media).length > 1 && (
          <div className={s.swiperButtons}>
            <div
              className={s.swiperBtnPrev}
              onClick={(e) => {
                e.preventDefault()
                instanceRef.current?.prev()
              }}
            >
              <IoChevronBack />
            </div>
            <div
              className={s.swiperBtnNext}
              onClick={(e) => {
                e.preventDefault()
                instanceRef.current.next()
              }}
            >
              <IoChevronForward />
            </div>
          </div>
        )}
      </div>
    </li>
  )
}

const shouldMediaCover = (media: any): boolean => {
  const mediaType = media.fileName?.split('_')[1]
  return (
    mediaType?.startsWith('V') ||
    mediaType?.startsWith('C') ||
    mediaType?.startsWith('L')
  )
}
export default ProductCardV2
